/**
 * public/AppImage WebP: 최초 fetch 후 IndexedDB + 메모리 Blob URL로 재사용.
 * 에셋이 거의 바뀌지 않으므로 버전 동일 시 네트워크를 건너뛴다.
 */

export const APP_IMAGE_FILES = [
  'character_0.webp',
  'character_1.webp',
  'character_2.webp',
  'character_3.webp',
] as const;

export type AppImageFile = (typeof APP_IMAGE_FILES)[number];

const DB_NAME = 'ClassPlannerAppImageCache';
const DB_VERSION = 1;
const STORE = 'images';

/** 에셋/변환 스펙이 바뀌면 올려서 재다운로드 */
export const APP_IMAGE_CACHE_VERSION = 'webp-v1';

type AppImageCacheRecord = {
  buffer: ArrayBuffer;
  version: string;
  mime: string;
};

type MemoryEntry = {
  blobUrl: string;
  version: string;
};

const memoryByKey = new Map<string, MemoryEntry>();
const inflightByKey = new Map<string, Promise<string | null>>();

function cacheKey(file: AppImageFile): string {
  return `v1:${file}`;
}

export function appImagePublicSrc(file: AppImageFile): string {
  return `${process.env.PUBLIC_URL || ''}/AppImage/${file}`;
}

function isAppImageFile(value: string): value is AppImageFile {
  return (APP_IMAGE_FILES as readonly string[]).includes(value);
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<AppImageCacheRecord | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<AppImageCacheRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as AppImageCacheRecord | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, record: AppImageCacheRecord): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(record, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function fetchImageArrayBuffer(
  sourceUrl: string
): Promise<{ buffer: ArrayBuffer; mime: string } | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    const mime =
      res.headers.get('content-type')?.split(';')[0]?.trim() || 'image/webp';
    return { buffer, mime };
  } catch {
    return null;
  }
}

function remember(key: string, version: string, buffer: ArrayBuffer, mime: string): string {
  const prev = memoryByKey.get(key);
  if (prev && prev.version !== version) {
    try {
      URL.revokeObjectURL(prev.blobUrl);
    } catch {
      /* ignore */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([buffer], { type: mime || 'image/webp' }));
  memoryByKey.set(key, { blobUrl, version });
  return blobUrl;
}

/** 동기 peek — 메모리에 같은 버전이 있으면 즉시 반환 */
export function peekCachedAppImageUrl(file: AppImageFile): string | null {
  if (!isAppImageFile(file)) return null;
  const key = cacheKey(file);
  const mem = memoryByKey.get(key);
  if (mem && mem.version === APP_IMAGE_CACHE_VERSION) return mem.blobUrl;
  return null;
}

/**
 * @returns Blob URL — 호출 측에서 revoke 하지 말 것(세션 동안 재사용).
 * 버전 동일 IDB hit면 네트워크를 타지 않는다.
 */
export async function getCachedAppImageUrl(file: AppImageFile): Promise<string | null> {
  if (!isAppImageFile(file)) return null;
  const key = cacheKey(file);
  const version = APP_IMAGE_CACHE_VERSION;

  const mem = memoryByKey.get(key);
  if (mem && mem.version === version) return mem.blobUrl;

  let inflight = inflightByKey.get(key);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(key);
        if (cached && cached.buffer?.byteLength > 0 && cached.version === version) {
          return remember(key, version, cached.buffer, cached.mime || 'image/webp');
        }
      } catch {
        /* IDB 미지원·거부 */
      }

      const got = await fetchImageArrayBuffer(appImagePublicSrc(file));
      if (!got) {
        try {
          const stale = await idbGet(key);
          if (stale?.buffer?.byteLength) {
            return remember(key, stale.version, stale.buffer, stale.mime || 'image/webp');
          }
        } catch {
          /* ignore */
        }
        return null;
      }

      try {
        await idbPut(key, {
          buffer: got.buffer,
          version,
          mime: got.mime,
        });
      } catch {
        /* 저장 실패해도 표시는 가능 */
      }
      return remember(key, version, got.buffer, got.mime);
    })().finally(() => {
      inflightByKey.delete(key);
    });
    inflightByKey.set(key, inflight);
  }
  return inflight;
}

/** 백그라운드 프리로드 — 이미 IDB에 있으면 네트워크 없음 */
export function preloadAppImages(files: readonly AppImageFile[] = APP_IMAGE_FILES): void {
  void Promise.all(files.map((file) => getCachedAppImageUrl(file)));
}
